import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { GetCommerceItemListParams, getCommerceItemList, getOneCommerceItem } from 'app/apis/CommerceServices';
import { PAGE_SIZE } from 'app/configs';
import { DriveFolderFormDataType, DRIVE_FOLDER_FORM_SCHEMA } from 'app/constants';
import { useAutoComplete } from 'app/hooks';
import { CommerceItemResponse } from 'app/models/CommerceItem';
import cn from 'app/utils/ClassName';

import Button from '../Button';
import {
  DateField,
  Form, ImageField, InputField,
  SelectField,
  TimeField,
} from '../Form';
import { SwitchField } from '../Form/SwitchField';
import Modal from '../Modal';

interface Props {
  visible: boolean;
  loading?: boolean;
  initialValue?: DriveFolderFormDataType;
  isFolder?: boolean;
  shopId?: number;
  onClose: () => void;
  onSubmit: (data: DriveFolderFormDataType) => Promise<void>;
}

export const DriveFolderFormModal = ({
  visible,
  loading,
  initialValue,
  isFolder = false,
  shopId,
  onClose,
  onSubmit,
}: Props) => {
  const methods = useForm<DriveFolderFormDataType>({
    defaultValues: {
      name: '',
      image: [],
      isAlbumMode: false,
      title: '',
      description: '',
      eventId: null,
      date: null,
      time: { hour: '', minute: '' },
      location: '',
    },
    resolver: zodResolver(DRIVE_FOLDER_FORM_SCHEMA),
  });

  const {
    handleSubmit, reset, control, setValue,
  } = methods;

  const isAlbumModeValue = useWatch({
    control, name: 'isAlbumMode',
  });
  const eventIdValue = useWatch({
    control, name: 'eventId',
  });

  useEffect(() => {
    const getEventInfo = async () => {
      if (eventIdValue) {
        const event = await getOneCommerceItem(eventIdValue);
        setValue('title', event.title || '', { shouldValidate: true });
        setValue('description', event.subtitle || '');
        setValue('location', event.EventDetail?.CommerceSchedule?.location || '');
        setValue('date', event.startDate ? dayjs(event.startDate).toDate() : null);
        setValue('time', { hour: event.startDate ? dayjs(event.startDate).format('HH') : '', minute: event.startDate ? dayjs(event.startDate).format('mm') : '' });
      }
    };
    getEventInfo();
  }, [eventIdValue, setValue]);

  const {
    setSearchText: setCommerceItemText,
    dataList: commerceItemList,
    onMenuScrollToBottom: onCommerceItemMenuScrollToBottom,
    setFilter,
  } = useAutoComplete<GetCommerceItemListParams, CommerceItemResponse>({
    getListCall: getCommerceItemList,
    getItemCall: (commerceItemId) => getOneCommerceItem(Number(commerceItemId)),
    labelRender: (commerceItem: CommerceItemResponse) => ({
      label: `#${commerceItem.commerceItemId} | ${commerceItem.title}`,
      value: commerceItem.commerceItemId,
    }),
    searchTextFieldName: 'title',
    selectedId: undefined,
    enabled: visible && isAlbumModeValue,
  });

  useEffect(() => {
    if (shopId) {
      setFilter({
        limit: PAGE_SIZE,
        page: 0,
        commerceType: '活動',
        shopId,
      });
    }
  }, [setFilter, shopId]);

  useEffect(() => {
    if (visible) {
      reset(initialValue);
    }
  }, [visible, initialValue, reset]);

  return (
    <Modal
      title={isFolder ? '資料夾' : '檔案'}
      visible={visible}
      onClose={() => {
        onClose();
        reset({
          name: '',
          image: [],
        });
      }}
      panelClassName="relative"
      containerClassName={cn('size-full p-0', isAlbumModeValue && !initialValue ? 'overflow-visible' : '')}
      size="xl"
    >
      <Form
        methods={methods}
        paddingBottom={16}
        className={isAlbumModeValue && !initialValue ? 'overflow-visible' : ''}
        innerClassName={isAlbumModeValue && !initialValue ? 'overflow-visible' : ''}
      >

        {isFolder && (
          <SwitchField
            name="isAlbumMode"
            label="Album Mode"
            className="col-span-2"
            onChangeCallback={() => {
              setValue('eventId', null);
            }}
          />
        )}
        <InputField
          name="name"
          label="資料夾名稱"
          containerClassName="col-span-2"
        />
        <div className="col-span-2 mb-4 h-px w-full bg-border-brand" />
        {isAlbumModeValue ? (
          <SelectField
            label="活動(可選擇已儲存活動，相關內容將會自動填寫)"
            name="eventId"
            placeholder="選擇"
            options={commerceItemList}
            onMenuScrollToBottom={onCommerceItemMenuScrollToBottom}
            onInputChange={setCommerceItemText}
            containerClassName="col-span-2"
          />
        ) : null}

        {
          isAlbumModeValue
            ? (
              <>

                <InputField
                  name="title"
                  label="名稱"
                  containerClassName="col-span-2"
                />
                <InputField
                  type="textarea"
                  name="description"
                  label="簡介"
                  containerClassName="col-span-2"
                  inputClassName="h-[200px]"
                />
                <InputField
                  name="location"
                  label="地點"
                  containerClassName="col-span-2"
                />
                <DateField
                  name="date"
                  label="日期"
                  containerClassName="col-span-2"
                />
                <TimeField
                  name="time"
                  label="時間"
                  containerClassName="col-span-2"
                />
              </>
            )
            : null
        }
        {isFolder && (
        <ImageField
          name="image"
          label="資料夾封面(選填)"
          className="col-span-2"
        />
        )}

        <div className="col-start-2 flex justify-end">
          <Button
            variant="contained"
            color="primary"
            containerStyle="px-[12px] py-[8px]"
            onClick={handleSubmit((data) => { onSubmit(data); })}
            loading={loading}
          >
            提交
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
